import * as React from "react";
import { cn } from "@/lib/utils";
import { Label } from "./Label";

export interface FormFieldContextValue {
  id: string;
  hintId?: string;
  errorId?: string;
  invalid: boolean;
  required: boolean;
}

export const FormFieldContext = React.createContext<FormFieldContextValue | null>(null);

/** Field ids and state from the surrounding <FormGroup>, if any. */
export function useFormField(): FormFieldContextValue | null {
  return React.useContext(FormFieldContext);
}

/** Merge a control's own props with the ids and ARIA state of its <FormGroup>. */
export function useFieldControlProps<
  P extends {
    id?: string;
    "aria-describedby"?: string;
    "aria-invalid"?: React.AriaAttributes["aria-invalid"];
    required?: boolean;
  },
>(props: P, invalid?: boolean): P {
  const field = useFormField();
  const isInvalid = invalid ?? field?.invalid ?? false;
  const describedBy = [props["aria-describedby"], field?.hintId, field?.errorId]
    .filter(Boolean)
    .join(" ");
  return {
    ...props,
    id: props.id ?? field?.id,
    required: props.required ?? field?.required,
    "aria-invalid": isInvalid || props["aria-invalid"] || undefined,
    "aria-describedby": describedBy || undefined,
  };
}

export interface FormGroupProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "id"> {
  label?: React.ReactNode;
  /** Helper text under the control. */
  hint?: React.ReactNode;
  /** Error message; marks the control invalid and replaces nothing else. */
  error?: React.ReactNode;
  required?: boolean;
  /** Control id. Generated when omitted. */
  id?: string;
}

/**
 * Label + control + hint + error, wired together: the Input/Select inside
 * receives the id, `aria-describedby`, `aria-invalid` and `required`.
 */
export function FormGroup({
  label,
  hint,
  error,
  required = false,
  id,
  className,
  children,
  ...props
}: FormGroupProps) {
  const generated = React.useId();
  const fieldId = id ?? `field-${generated}`;
  const value: FormFieldContextValue = {
    id: fieldId,
    hintId: hint ? `${fieldId}-hint` : undefined,
    errorId: error ? `${fieldId}-error` : undefined,
    invalid: Boolean(error),
    required,
  };
  return (
    <FormFieldContext.Provider value={value}>
      <div className={cn("flex flex-col gap-2", className)} {...props}>
        {label && (
          <Label htmlFor={fieldId} required={required}>
            {label}
          </Label>
        )}
        {children}
        {hint && (
          <p
            id={value.hintId}
            className="font-body text-[length:var(--font-size-xs)] text-ds-text-muted"
          >
            {hint}
          </p>
        )}
        {error && (
          <p
            id={value.errorId}
            role="alert"
            className="font-body text-[length:var(--font-size-xs)] font-semibold text-ds-text-error"
          >
            {error}
          </p>
        )}
      </div>
    </FormFieldContext.Provider>
  );
}
